import type { GoalActivityInitiative, GoalActivityProject, WorkOrdering } from '@localagent/sdk';

export interface RoadmapEdge {
  /** Must reach a terminal state first (`blocking_node_id`). */
  from: string;
  /** Waits (`blocked_node_id`). */
  to: string;
}

export function initiativeEdges(orderings: WorkOrdering[] | undefined): RoadmapEdge[] {
  return (orderings ?? [])
    .filter((ordering) => ordering.node_kind === 'initiative')
    .map((ordering) => ({ from: ordering.blocking_node_id, to: ordering.blocked_node_id }));
}

export interface RoadmapRanking {
  /** Stage index → the node ids at that rank, in the order they were supplied. */
  stages: string[][];
  rankOf: Map<string, number>;
  /** Kahn's leftovers: nodes in a cycle, downstream of one, or waiting on an absent blocker. */
  residual: string[];
  /** The longest dependency chain, source-first; empty when nothing chains. */
  criticalPath: string[];
}

function longestChain(
  stages: string[][],
  incoming: Map<string, string[]>,
  rankOf: Map<string, number>,
): string[] {
  const chainLength = new Map<string, number>();
  const parent = new Map<string, string | null>();
  let end: string | null = null;

  for (const stage of stages) {
    for (const id of stage) {
      let bestPredecessor: string | null = null;
      let bestLength = 0;
      for (const predecessor of incoming.get(id) ?? []) {
        if (!rankOf.has(predecessor)) continue;
        const length = chainLength.get(predecessor)!;
        if (length > bestLength) {
          bestLength = length;
          bestPredecessor = predecessor;
        }
      }
      chainLength.set(id, bestLength + 1);
      parent.set(id, bestPredecessor);
      if (end === null || chainLength.get(id)! > chainLength.get(end)!) end = id;
    }
  }

  if (end === null || chainLength.get(end)! < 2) return [];

  const chain: string[] = [];
  for (let cursor: string | null = end; cursor !== null; cursor = parent.get(cursor) ?? null) {
    chain.push(cursor);
  }
  return chain.reverse();
}

/**
 * (work_management ADR-031).
 */
export function rankRoadmap(nodeIds: string[], edges: RoadmapEdge[]): RoadmapRanking {
  const known = new Set(nodeIds);
  const indegree = new Map<string, number>(nodeIds.map((id) => [id, 0]));
  const outgoing = new Map<string, string[]>();
  const incoming = new Map<string, string[]>();

  for (const edge of edges) {
    if (!known.has(edge.to)) continue;
    indegree.set(edge.to, indegree.get(edge.to)! + 1);
    if (!known.has(edge.from)) continue;
    const out = outgoing.get(edge.from);
    if (out) out.push(edge.to);
    else outgoing.set(edge.from, [edge.to]);
    const inc = incoming.get(edge.to);
    if (inc) inc.push(edge.from);
    else incoming.set(edge.to, [edge.from]);
  }

  const stages: string[][] = [];
  const rankOf = new Map<string, number>();
  let frontier = nodeIds.filter((id) => indegree.get(id) === 0);

  while (frontier.length > 0) {
    for (const id of frontier) rankOf.set(id, stages.length);
    stages.push(frontier);
    const next: string[] = [];
    for (const id of frontier) {
      for (const dependent of outgoing.get(id) ?? []) {
        const remaining = indegree.get(dependent)! - 1;
        indegree.set(dependent, remaining);
        if (remaining === 0) next.push(dependent);
      }
    }
    frontier = next;
  }

  return {
    stages,
    rankOf,
    residual: nodeIds.filter((id) => !rankOf.has(id)),
    criticalPath: longestChain(stages, incoming, rankOf),
  };
}

export interface InitiativeMilestone {
  total: number;
  accepted: number;
  filled: boolean;
}

export function projectAccepted(
  project: Pick<GoalActivityProject, 'state' | 'verification_state'>,
): boolean {
  return project.state === 'completed' && project.verification_state === 'accepted';
}

export function initiativeMilestone(
  initiative: Pick<GoalActivityInitiative, 'projects'>,
): InitiativeMilestone {
  const total = initiative.projects.length;
  const accepted = initiative.projects.filter(projectAccepted).length;
  return { total, accepted, filled: total > 0 && accepted === total };
}

export function stageLabel(index: number): string {
  return `Stage ${index + 1}`;
}

export const NO_ORDERING_HEADLINE = 'No ordering recorded yet';

export const NO_ORDERING_DETAIL =
  'These initiatives carry no dependency edges, so no stage order can be shown. Ask the agent to record what must finish before what.';

export const CANNOT_ORDER_HEADLINE = 'Cannot order';

export const CANNOT_ORDER_DETAIL =
  'These initiatives depend on each other in a loop, or wait on something this goal does not carry.';

export const NO_CRITICAL_PATH_LABEL = 'No dependency chain';

export function criticalPathSummary(
  criticalPath: string[],
  rankOf: Map<string, number>,
): string {
  if (criticalPath.length === 0) return NO_CRITICAL_PATH_LABEL;
  const first = rankOf.get(criticalPath[0])!;
  const last = rankOf.get(criticalPath[criticalPath.length - 1])!;
  return `${criticalPath.length} initiatives, ${stageLabel(first)}–${stageLabel(last)}`;
}
